import { useEffect, useRef, useState, type JSX } from 'react';
import { subscribeDoneTickets } from './ticketsApi';

const ROLL_MS = 700;

// Framed odometer that rolls to its value instead of snapping. The first value
// is seeded without animation; later changes spin up like a speedometer.
function Counter({
  value,
  label,
  icon,
  title,
}: {
  value: number;
  label: string;
  icon: string;
  title: string;
}): JSX.Element {
  const [display, setDisplay] = useState(value);
  const displayRef = useRef(value);
  const seeded = useRef(false);
  const [rolling, setRolling] = useState(false);
  const rafRef = useRef(0);

  useEffect(() => {
    // First render seeds the value (no roll-up from zero on mount).
    if (!seeded.current) {
      seeded.current = true;
      displayRef.current = value;
      setDisplay(value);
      return;
    }
    cancelAnimationFrame(rafRef.current);
    const from = displayRef.current;
    const to = value;
    if (from === to) return;
    setRolling(true);
    let start: number | null = null;
    const step = (ts: number): void => {
      if (start === null) start = ts;
      const t = Math.min(1, (ts - start) / ROLL_MS);
      // easeOutCubic - quick spin that settles, like a needle coming to rest.
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (to - from) * eased);
      displayRef.current = v;
      setDisplay(v);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else setRolling(false);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [value]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <span className="done-counter" title={title}>
      <i className={`fas ${icon}`} />
      <span className="done-counter-label">{label}:</span>
      <span className={`done-counter-num ${rolling ? 'rolling' : ''}`}>{display}</span>
    </span>
  );
}

// Completed-ticket tally left of the nickname, fed by a live done-ticket
// subscription. A per-user "Me" counter is planned but hidden for now (pass
// userEmail back in and filter rows by assignee to bring it back).
export function DoneCounter(): JSX.Element | null {
  const [rows, setRows] = useState<{ assignee?: string }[] | null>(null);

  useEffect(() => subscribeDoneTickets(setRows), []);

  // Wait for the first snapshot so the counter seeds at its real value rather
  // than rolling up from zero on load.
  if (!rows) return null;

  const total = rows.length;

  return (
    <Counter value={total} label="Total" icon="fa-trophy" title="Completed tickets (all)" />
  );
}
